import { Component, Input, inject } from "@angular/core";
import { AuditFindingLinkModel } from "@app/audit-finding/shared/data-models/audit-finding-link-model";
import { AuditDataService } from "@app/audit/services/audit-data.service";
import { GenericListConfig } from "@app/shared/models/GenericList/GenericList";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { StandingdataDataService } from "@app/standingdata/shared/standingdata.service";
import { Pages } from "@constants/pages/Pages";
import { FormatType } from "@enums/FormatType";
import { RendererType } from "@enums/RendererType";
import { StandingDataType } from "@enums/StandingDataType";
import { nameof } from "@methods/jeffs-toolkit";
import { LinkedMoiModel } from "@models/moi/LinkedMoiModel";
import { TabService } from "@services/tabs/TabService";
import { Observable } from "rxjs";
import { injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/findingreport/i18n/scope";
import { ExpandableListComponent } from "@app/shared/components/expandable-list/expandable-list.component";

@Component({
    selector: "finding-report-findings",
    templateUrl: "./finding-report-findings.component.html",
    imports: [ExpandableListComponent],
    viewProviders: [provideTranslationScope(scope)],
})
export class FindingReportsFindingsComponent {
    private _ds = inject(AuditDataService);
    private _sdDs = inject(StandingdataDataService);
    private _tabService = inject(TabService);
    private _pages = inject(Pages);
    private _translation = injectTranslation(scope);

    @Input() auditGuid: string;

    mainConfig: GenericListConfig;
    innerConfig: GenericListConfig;
    innerConfigDataMethod: (guid: string) => Observable<any>;

    async init() {
        // The list configs hold translated text.
        await this._translation.ready();
        if (!this.mainConfig) {
            this.initMainConfig();
            this.initInnerConfig();
        }
    }

    initMainConfig() {
        this.mainConfig = {
            name: this.t("title"),
            idProp: "guid",

            allowAdd: false,
            allowDelete: false,
            allowEdit: false,

            fields: [
                {
                    prettyName: this.t("identifier"),
                    fieldName: nameof<AuditFindingLinkModel>((x) => x.identifier),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    prettyName: this.t("findingDescription"),
                    fieldName: nameof<AuditFindingLinkModel>((x) => x.findingDescription),
                    fieldType: GenericListFieldType.TextArea,
                },
                {
                    prettyName: this.t("recommendation"),
                    fieldName: nameof<AuditFindingLinkModel>((x) => x.recommendation),
                    fieldType: GenericListFieldType.TextArea,
                },
                {
                    prettyName: this.t("severity"),
                    fieldName: nameof<AuditFindingLinkModel>((x) => x.severity),
                    fieldType: GenericListFieldType.ColoredSelect,
                    getDataMethod: () => {
                        return this._sdDs.getAllByType(StandingDataType.AuditSeverity);
                    },
                },
            ],

            dataMethod: () => {
                return this._ds.getAuditLinkedFindings(this.auditGuid);
            },
            overviewRowActions: [],
        };
        this.mainConfig.overviewRowActions.push({
            icon: "fas fa-external-link-alt",
            tooltip: this.t("openDetails"),
            clickEvent: (row: any) => {
                this._tabService.generateTab(this._pages.AuditFindingDetail, row.guid);
            },
        });
    }

    initInnerConfig() {
        this.innerConfig = <GenericListConfig>{
            name: this.t("linkedMoisTitle"),
            idProp: "guid",

            allowAdd: false,
            allowEdit: false,
            allowLink: false,

            fields: [
                {
                    prettyName: this.t("identifier"),
                    fieldName: nameof<LinkedMoiModel>((x) => x.identifier),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    prettyName: this.t("name"),
                    fieldName: nameof<LinkedMoiModel>((x) => x.name),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    prettyName: this.t("organizationResponsible"),
                    fieldName: nameof<LinkedMoiModel>((x) => x.organizationResponsible),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    prettyName: this.t("responsible"),
                    fieldName: nameof<LinkedMoiModel>((x) => x.responsible),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    prettyName: this.t("status"),
                    fieldName: nameof<LinkedMoiModel>((x) => x.status),
                    fieldType: GenericListFieldType.Text,
                },
                {
                    prettyName: this.t("dueDate"),
                    fieldName: nameof<LinkedMoiModel>((x) => x.dueDate),
                    fieldType: GenericListFieldType.Date,
                },
                {
                    prettyName: this.t("progress"),
                    fieldName: nameof<LinkedMoiModel>((x) => x.progress),
                    fieldType: GenericListFieldType.Text,
                },
            ],

            rendererConfig: [
                {
                    textColumn: nameof<LinkedMoiModel>((x) => x.dueDate),
                    actionColumn: nameof<LinkedMoiModel>((x) => x.isOverdue),
                    actionValue: "Yes",
                    type: RendererType.DueDate,
                    formatType: FormatType.DateFormat,
                },
                {
                    textColumn: nameof<LinkedMoiModel>((x) => x.progress),
                    type: RendererType.Progress,
                },
            ],
            overviewRowActions: [],
        };
        this.innerConfig.overviewRowActions.push({
            icon: "fas fa-external-link-alt",
            tooltip: this.t("openDetails"),
            clickEvent: (row: LinkedMoiModel) => {
                this._tabService.generateTab(this._pages.MoiDetail, row.guid);
            },
        });
        this.innerConfigDataMethod = this._ds.getLinkedFindingsMois.bind(this._ds);
    }

    private t(key: string): string {
        return this._translation.t(`findings.${key}`);
    }
}
